import { useForm, useStore } from '@tanstack/react-form';
import { translate } from 'src/app/project/features/translate/translate';
import VDialogPW from '@voly/ui-components/src/v2/VDialogPW';
import VStack from '@voly/ui-components/src/v2/VStack';
import VInput from '@voly/ui-components/src/v2/VInput';
import VSignature from '@voly/ui-components/src/v2/VSignature/VSignature';
import VFormField from '@voly/ui-components/src/v2/VFormField';
import VFormErrorSummary from '@voly/ui-components/src/v2/VFormErrorSummary';

import { ReactElement } from 'react';
import { z } from 'zod';
import VStackRow from '@voly/ui-components/src/v2/VStackRow';

export type TDigitalSignatureValues = {
  name: string;
  role: string;
  svg: File;
};

type TSignatureForm = {
  name: string;
  role: string;
  svg: File | null;
};

type TFieldMeta = {
  isTouched: boolean;
  errors: unknown[];
};

interface AddDigitalSignatureModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: TDigitalSignatureValues) => void;
  saveButtonText?: string;
  user?: TDigitalSignatureValues;
}

const toMessage = (issue: unknown): string | undefined =>
  typeof issue === 'object' && issue !== null && 'message' in issue
    ? String(issue.message)
    : undefined;

const errorFor = (meta?: TFieldMeta): string | undefined =>
  meta?.isTouched ? toMessage(meta.errors[0]) : undefined;

const hasErrors = (meta?: TFieldMeta): boolean => (meta?.errors.length ?? 0) > 0;

const toFieldErrors = (fieldMeta: Partial<Record<keyof TSignatureForm, TFieldMeta>>): string[] => {
  const isNameMissing = hasErrors(fieldMeta.name);
  const isSignatureMissing = hasErrors(fieldMeta.svg);

  if (isNameMissing && isSignatureMissing) {
    return [translate('signature_name_and_signature_required')];
  }

  if (isSignatureMissing) {
    return [translate('signature_required')];
  }

  if (isNameMissing) {
    return [translate('signature_name_required')];
  }

  return [];
};

const toSignatureFormSchema = (): z.ZodType<TDigitalSignatureValues, TSignatureForm> =>
  z.object({
    name: z.string().trim().min(1, translate('signature_name_required')),
    role: z.string().trim(),
    svg: z.instanceof(File, { message: translate('signature_required') }),
  });

const AddDigitalSignatureModal = ({
  onClose,
  onSave,
  open,
  saveButtonText,
  user,
}: AddDigitalSignatureModalProps): ReactElement => {
  const defaultValues: TSignatureForm = {
    name: user?.name ?? '',
    role: user?.role ?? '',
    svg: null,
  };

  const form = useForm({
    defaultValues: defaultValues,
    validators: { onChange: toSignatureFormSchema() },
    onSubmit: ({ value }) => {
      if (!value.svg) {
        return;
      }

      onSave({
        name: value.name.trim(),
        role: value.role.trim(),
        svg: value.svg,
      });
      form.reset();
    },
  });

  const fieldMeta = useStore(form.store, (state) => state.fieldMeta);
  const saveAttempted = useStore(form.store, (state) => state.submissionAttempts > 0);
  const fieldErrors = saveAttempted ? toFieldErrors(fieldMeta) : [];

  const visibleErrorFor = (meta?: TFieldMeta): string | undefined =>
    saveAttempted ? errorFor(meta) : undefined;

  const closeAndReset = (): void => {
    form.reset();
    onClose();
  };

  const handleOpenChange = (details: { open: boolean }): void => {
    if (!details.open) {
      closeAndReset();
    }
  };

  return (
    <VDialogPW
      open={open}
      size="lg"
      onOpenChange={handleOpenChange}
      onOk={() => form.handleSubmit()}
      onCancel={closeAndReset}
      loading={false}
      saveButtonDisabled={fieldErrors.length > 0}
      title={translate('add_your_signature')}
      body={
        <VStack gap="4">
          <form.Field name="svg">
            {(field) => (
              <VSignature
                readOnly={false}
                invalid={Boolean(visibleErrorFor(field.state.meta))}
                prefixHelperText={{
                  draw: translate('draw_description'),
                  type: translate('type_description'),
                }}
                onChange={(svg) => {
                  if (svg === field.state.value) {
                    return;
                  }

                  field.handleChange(svg);
                }}
              />
            )}
          </form.Field>

          <VStackRow>
            <form.Field name="name">
              {(field) => (
                <VFormField
                  label={translate('signature_name')}
                  helperText={undefined}
                  invalid={Boolean(visibleErrorFor(field.state.meta))}
                >
                  <VInput
                    aria-label={translate('signature_name')}
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    placeholder="John Doe"
                  />
                </VFormField>
              )}
            </form.Field>

            <form.Field name="role">
              {(field) => (
                <VFormField
                  optional
                  label={translate('signature_role')}
                  helperText={undefined}
                  invalid={Boolean(visibleErrorFor(field.state.meta))}
                  errorText={visibleErrorFor(field.state.meta)}
                >
                  <VInput
                    aria-label={translate('signature_role')}
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    placeholder="Manager"
                  />
                </VFormField>
              )}
            </form.Field>
          </VStackRow>
          <VFormErrorSummary errors={fieldErrors} />
        </VStack>
      }
      saveBtnText={saveButtonText ? saveButtonText : translate('save_signature')}
    />
  );
};

export default AddDigitalSignatureModal;
